// MapEditor.js
import React, { useState, useEffect } from 'react';
import axios from '../../api/axiosInstance';
import styles from './MapEditor.module.css';
import showModal from '../ui/modals/showModal';

const HOST = process.env.REACT_APP_HOST;
const PORT = process.env.REACT_APP_API_PORT;
const baseUrl = `https://${HOST}/api/admin`;


function MapEditor() {
  const [cols, setCols] = useState(6);
  const [rows, setRows] = useState(19);
  const [grid, setGrid] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('Piętro -1');
  const [floors, setFloors] = useState([]);
  const [previewReady, setPreviewReady] = useState(false);
  const [backup, setBackup] = useState(null);


  useEffect(() => {
    const fetchBackup = async () => {
      try {
        const res = await axios.get(`${baseUrl}/parking-spots`);
        setBackup(res.data);
        window.__mapBackup = res.data;
      } catch (err) {
        logger.error('❌ Błąd pobierania backupu:', err);
      }
    };
    fetchBackup();
  }, []);
  useEffect(() => {
    const fetchFloors = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${baseUrl}/floors`);
        setFloors(res.data);
        if (res.data.length > 0) setSelectedFloor(res.data[0]);
      } catch (err) {
        logger.error('❌ Błąd przy pobieraniu pięter:', err);
      }
    };
  
    fetchFloors();
  }, []);

  const handleGenerate = () => {
    const newGrid = Array.from({ length: rows }, () => Array(cols).fill(''));
    setGrid(newGrid);
    setPreviewReady(true);
  };

  const handleLoadAndEdit = () => {
    const filtered = backup?.filter(s => s.floor === selectedFloor) || [];
    if (filtered.length === 0) {
      showModal('❗ To piętro nie ma żadnych miejsc.', { type: 'warning' });
      return;
    }
  
    const maxX = Math.max(...filtered.map(s => s.x_position));
    const maxY = Math.max(...filtered.map(s => s.y_position));
    const columnWidths = [];
  
    let x = 0;
    while (x <= maxX) {
      const colSpots = filtered.filter(s => s.x_position === x);
      columnWidths.push(colSpots.length > 0 ? 100 : 30);
      x += columnWidths[columnWidths.length - 1];
    }
  
    const estimatedCols = columnWidths.length;
    const estimatedRows = Math.ceil(maxY / 60);
    const newGrid = Array.from({ length: estimatedRows }, () => Array(estimatedCols).fill(''));
  
    filtered.forEach(spot => {
      const colIndex = columnWidths.reduce((acc, w, i) => acc >= 0 ? acc : (spot.x_position === columnWidths.slice(0, i).reduce((a, b) => a + b, 0) ? i : -1), -1);
      const rowIndex = Math.floor(spot.y_position / 60) - 1;
      if (rowIndex >= 0 && rowIndex < estimatedRows && colIndex >= 0 && colIndex < estimatedCols) {
        newGrid[rowIndex][colIndex] = spot.spotNumber.toString();
      }
    });
    setCols(estimatedCols);
    setRows(estimatedRows);
    setGrid(newGrid);
    setPreviewReady(true);
  };
  const handleChange = (r, c, value) => {
    const newGrid = [...grid];
    newGrid[r][c] = value;
    setGrid(newGrid);
  };

  const buildColumnWidths = (data) => {
    return data[0].map((_, colIndex) => {
      const isEmpty = data.every(row => !row[colIndex]);
      return isEmpty ? 30 : 100;
    });
  };

  const columnIsEmpty = (colIndex) => {
    return grid.every(row => !row[colIndex]);
  };

  const formatGridDebug = (grid) => {
    return '[\n' + grid.map(row =>
      '  [' + row.map(cell => (cell === '' ? 'null' : cell)).join(', ') + ']'
    ).join(',\n') + '\n]';
  };

  const renderMapPreview = () => {
    const columnWidths = buildColumnWidths(grid); // 👈 dodajemy tutaj!
    return (
      <div
        className={styles.previewMap}
        style={{
          gridTemplateColumns: columnWidths.map(w => `${w * 0.6}px`).join(' ')
        }}
      >
        {grid.flat().map((cell, idx) => (
          <div
            key={idx}
            className={cell ? styles.previewCell + ' ' + styles.filled : styles.previewCell + ' ' + styles.empty}
          >
            {cell || ''}
          </div>
        ))}
      </div>
    );
  };

  const handleSaveAsNew = async () => {
    if (!window.confirm('Ta operacja całkowicie nadpisze tabelę miejsc i nada nowe ID. Kontynuować?')) return;

    const csvGrid = grid.map(row => row.map(cell => cell === '' ? null : parseInt(cell)));
    const columnWidths = buildColumnWidths(csvGrid);
    const rowHeight = 60;
    const now = new Date().toISOString();
    const spots = [];

    csvGrid.forEach((row, rowIndex) => {
      let y = (rowIndex + 1) * rowHeight;
      let x = 0;
      const xMap = columnWidths.map(w => (x += w) - w);
      row.forEach((val, colIndex) => {
        if (val !== null && !isNaN(val)) {
          spots.push({
            spotNumber: parseInt(val),
            x_position: xMap[colIndex],
            y_position: y,
            createdAt: now,
            updatedAt: now
          });
        }
      });
    });

    try {
      await axios.post(`${baseUrl}/overwrite-parking-spots`, { spots, floor: selectedFloor });
      showModal('✅ Zapisano jako nowe miejsca.', {type: "success"});
    } catch (err) {
      showModal('❌ Błąd przy zapisie.', {type: "error"});
      logger.error(err);
    }
  };

  const handleUpdateDifferences = async () => {
    let current;
    try {
      const res = await axios.get(`${baseUrl}/parking-spots`);
      current = res.data;
    } catch (err) {
      showModal('❌ Nie udało się pobrać aktualnych danych z bazy', {type: "error"});
      return;
    }

    const csvGrid = grid.map(row => row.map(cell => cell === '' ? null : parseInt(cell)));
    const columnWidths = buildColumnWidths(csvGrid);
    const rowHeight = 60;
    const now = new Date().toISOString();
    const updates = [];

    csvGrid.forEach((row, rowIndex) => {
      let y = (rowIndex + 1) * rowHeight;
      let x = 0;
      const xMap = columnWidths.map(w => (x += w) - w);
      row.forEach((val, colIndex) => {
        if (val !== null && !isNaN(val)) {
          const existing = current.find(s => s.spotNumber === val);
          if (existing) {
            if (
              existing.x_position !== xMap[colIndex] ||
              existing.y_position !== y ||
              existing.spotNumber !== val
            ) {
              updates.push({
                id: existing.id,
                spotNumber: val,
                x_position: xMap[colIndex],
                y_position: y,
                updatedAt: now
              });
            }
          } else {
            updates.push({
              spotNumber: val,
              x_position: xMap[colIndex],
              y_position: y,
              createdAt: now,
              updatedAt: now
            });
          }
        }
      });
    });

    const currentSpotNumbers = current.map(s => s.spotNumber);
    const updatedSpotNumbers = csvGrid.flat().filter(Boolean);
    const deletions = current
      .filter(s => !updatedSpotNumbers.includes(s.spotNumber))
      .map(s => s.id);

    try {
      await axios.put(`${baseUrl}/update-parking-differences`, {
        updates,
        deletions,
        floor: selectedFloor
      });
      showModal('✅ Zaktualizowano różnice (w tym usunięcia).', {type: "success"});
    } catch (err) {
      showModal('❌ Błąd przy aktualizacji różnic.', {type: "error"});
      logger.error(err);
    }
  };

  const handleRestore = async () => {
    if (!backup || !Array.isArray(backup) || backup.length === 0) {
      showModal('⚠️ Brak danych backupu do przywrócenia.', {type: "error"});
      return;
    }

    try {
      await axios.post(`${baseUrl}/overwrite-parking-spots`, { spots: backup });
      showModal('🔁 Przywrócono poprzednie miejsca.', {type: "success"});
    } catch (err) {
      showModal('❌ Błąd przy przywracaniu.', {type: "error"});
      logger.error(err);
    }
  };

  return (
    <div>
      <h3 className={styles.editorHeader}>🛠 Edycja mapy miejsc</h3>
      <div className={styles.editorControls}>
        <label>
          Liczba kolumn:
          <input type="number" value={cols} onChange={e => setCols(+e.target.value)} />
        </label>
        <label>
          Liczba wierszy:
          <input type="number" value={rows} onChange={e => setRows(+e.target.value)} />
        </label>
      </div>
            <div className={styles.editorControls}>
        <div className={styles.floorControls}>
          <label>
            Piętro:
            <select value={selectedFloor} onChange={(e) => setSelectedFloor(e.target.value)}>
              {floors.map((f, idx) => (
                <option key={idx} value={f}>{f}</option>
              ))}
            </select>
          </label>

          <button className={styles.floorButton} onClick={async () => {
            const name = prompt('🆕 Podaj nazwę nowego piętra:');
            if (!name) return;
            setFloors(prev => [...prev, name]);
            setSelectedFloor(name);
            setGrid([]);
            setPreviewReady(false);
          }}>
            ➕ Dodaj piętro
          </button>

          <button className={styles.floorButton} onClick={async () => {
            const newName = prompt(`✏️ Zmień nazwę piętra "${selectedFloor}" na:`);
            if (!newName || newName === selectedFloor) return;
            try {
              const token = localStorage.getItem('token');
              await axios.put(`${baseUrl}/rename-floor`, {
                oldName: selectedFloor,
                newName
              });
              const updatedFloors = floors.map(f => f === selectedFloor ? newName : f);
              setFloors(updatedFloors);
              setSelectedFloor(newName);
              showModal('✅ Zmieniono nazwę piętra', { type: 'success' });
            } catch (err) {
              showModal('❌ Nie udało się zmienić nazwy piętra', { type: 'error' });
              logger.error(err);
            }
          }}>
            ✏️ Zmień nazwę
          </button>
          <button className={styles.floorButton} onClick={async () => {
            if (!window.confirm(`Na pewno chcesz usunąć piętro "${selectedFloor}" wraz z miejscami i rezerwacjami?`)) return;
            try {
              const token = localStorage.getItem('token');
              await axios.delete(`${baseUrl}/floor`, {
                data: { floor: selectedFloor }
              });
              const updatedFloors = floors.filter(f => f !== selectedFloor);
              setFloors(updatedFloors);
              setSelectedFloor(updatedFloors[0] || '');
              setGrid([]);
              setPreviewReady(false);
              showModal('✅ Piętro usunięte.', { type: 'success' });
            } catch (err) {
              showModal('❌ Nie udało się usunąć piętra.', { type: 'error' });
              logger.error(err);
            }
          }}>
            🗑️ Usuń piętro
          </button>
        </div>

        <div className={styles.buttonsWrapper}>
          <button className={styles.generateButton} onClick={handleGenerate}>🧱 Generuj siatkę</button>
          <button className={styles.generateButton} onClick={handleLoadAndEdit}>📥 Załaduj i edytuj</button>
          <button className={styles.generateButton} onClick={handleSaveAsNew}>🆕 Zapisz jako nowe</button>
          <button className={styles.generateButton} onClick={handleUpdateDifferences}>📝 Zapisz różnice</button>
          {backup && (
            <button className={styles.restoreButton} onClick={handleRestore}>🔁 Przywróć poprzednie</button>
          )}
        </div>
      </div>


      {previewReady && (
        <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
          <table className={styles.gridTable}>
            <tbody>
              {grid.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((val, cIdx) => (
                    <td key={cIdx} className={columnIsEmpty(cIdx) ? styles.emptyCol : ''}>
                      <input type="text" value={val} onChange={e => handleChange(rIdx, cIdx, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <h4 style={{ marginTop: '1rem' }}>🔍 Podgląd (debug):</h4>
          <pre className={styles.debugBlock}>{formatGridDebug(grid)}</pre>
          <h4 style={{ marginTop: '1rem' }}>🗺️ Wizualizacja mapy:</h4>
          {renderMapPreview()}
        </div>
      )}
    </div>
  );
}

export default MapEditor;
